import React, { useRef, useState } from 'react';
import { X, Upload } from 'lucide-react';
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { db } from '../db';
import { supabase } from '../lib/supabase';
import type { SKU } from '../types'; // SKU 타입으로 통일

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ImportModal({ isOpen, onClose }: ImportModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false); // 업로드 상태 추가

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('로그인이 필요합니다');

      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');

      const sizes: string[] = [];
      const sizeColumns: number[] = [];

      // 헤더 행에서 사이즈 정보 수집
      for (let col = 2; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        const cell = worksheet[cellAddress];
        if (cell && cell.v) {
          const headerValue = cell.v.toString().trim();
          if (!headerValue.toLowerCase().startsWith('to')) {
            sizes.push(headerValue);
            sizeColumns.push(col);
          }
        }
      }

      const skuMap = new Map<string, Partial<SKU>>();

      // 데이터 행 처리
      for (let row = 1; row <= range.e.r; row++) {
        const styleNo = worksheet[XLSX.utils.encode_cell({ r: row, c: 0 })]?.v;
        const color = worksheet[XLSX.utils.encode_cell({ r: row, c: 1 })]?.v;

        if (!styleNo || !color) continue;

        for (let i = 0; i < sizes.length; i++) {
          const quantity = worksheet[XLSX.utils.encode_cell({ r: row, c: sizeColumns[i] })]?.v;
          if (quantity && !isNaN(Number(quantity)) && Number(quantity) > 0) {
            const skuId = `${styleNo.toString().trim()}-${color.toString().trim()}-${sizes[i]}`;
            const currentQuantity = Number(quantity);

            if (skuMap.has(skuId)) {
              const existing = skuMap.get(skuId)!;
              skuMap.set(skuId, {
                ...existing,
                expectedQuantity: (existing.expectedQuantity || 0) + currentQuantity,
              });
            } else {
              skuMap.set(skuId, {
                id: skuId,
                styleNo: styleNo.toString().trim(),
                color: color.toString().trim(),
                size: sizes[i],
                expectedQuantity: currentQuantity,
                actualQuantity: 0,
                status: 'pending',
                progress: 0,
              });
            }
          }
        }
      }

      const skuData = Array.from(skuMap.values());
      if (skuData.length === 0) {
        throw new Error('유효한 데이터가 없습니다. 엑셀 형식을 확인해주세요.');
      }

      const skusToAdd: SKU[] = skuData.map((row) => ({
        ...row,
        lastScanned: new Date(),
        lastModified: new Date(),
        userId: session.user.id,
      })) as SKU[];

      const batchSize = 50;
      const batches = [];
      for (let i = 0; i < skusToAdd.length; i += batchSize) {
        batches.push(skusToAdd.slice(i, i + batchSize));
      }

      let insertedCount = 0;
      let updatedCount = 0;

      // Supabase 배치 처리
      for (const batch of batches) {
        const supabaseBatch = batch.map((sku) => ({
          id: sku.id,
          style_no: sku.styleNo, // Supabase 필드 이름 맞춤
          color: sku.color,
          size: sku.size,
          expected_quantity: sku.expectedQuantity,
          actual_quantity: sku.actualQuantity,
          status: sku.status,
          progress: sku.progress,
          last_scanned: sku.lastScanned.toISOString(),
          last_modified: sku.lastModified.toISOString(),
          user_id: session.user.id,
        }));

        if (navigator.onLine) {
          const { data: inserted, error: insertError } = await supabase
            .from('skus')
            .upsert(supabaseBatch, { onConflict: 'id', ignoreDuplicates: true })
            .select();

          if (insertError) throw new Error(`Supabase 삽입 실패: ${insertError.message}`);

          insertedCount += inserted?.length || 0;

          const { error: updateError } = await supabase
            .from('skus')
            .upsert(
              supabaseBatch.map((sku) => ({
                ...sku,
                actual_quantity: 0,
                status: 'pending',
                progress: 0,
                last_modified: new Date().toISOString(),
              })),
              { onConflict: 'id' }
            );

          if (updateError) throw new Error(`Supabase 업데이트 실패: ${updateError.message}`);
          updatedCount += batch.length - (inserted?.length || 0);
        }

        // 로컬 DB 업데이트 (온라인/오프라인 모두)
        await db.transaction('rw', db.skus, async () => {
          await db.skus.bulkPut(batch);
        });
      }

      // 로그 추가
      await db.transaction('rw', db.logs, async () => {
        await db.logs.add({
          id: crypto.randomUUID(),
          timestamp: new Date(),
          type: 'import',
          message: `${skuData.length}개의 SKU 데이터가 임포트되었습니다`,
          metadata: {
            totalSKUs: skuData.length,
            totalQuantity: skuData.reduce((sum, row) => sum + (row.expectedQuantity || 0), 0),
            fileName: file.name,
            newSkus: insertedCount,
            updatedSkus: updatedCount,
          },
        });
      });

      const totalQuantity = skuData.reduce((sum, row) => sum + (row.expectedQuantity || 0), 0);
      toast.success(
        `임포트 완료:\n- 총 ${skuData.length}개 SKU\n- 신규: ${insertedCount}개\n- 업데이트: ${updatedCount}개\n- 총 수량: ${totalQuantity.toLocaleString()}개`,
        { duration: 5000 }
      );

      onClose();
    } catch (error: any) {
      console.error('파일 처리 중 오류:', error.message);
      toast.error(error.message || '파일 처리에 실패했습니다');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold text-gray-900">입고 예정 데이터 업로드</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
            disabled={isUploading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4">
          <div className="mb-4 p-4 bg-blue-50 rounded-lg">
            <h3 className="font-medium text-blue-800 mb-2">엑셀 파일 형식 안내</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• A열: 스타일 번호</li>
              <li>• B열: 컬러</li>
              <li>• C열 이후: 각 사이즈별 수량</li>
              <li>• 첫 번째 행: 사이즈 정보 (C열부터)</li>
              <li>• 'To'로 시작하는 열은 합계열로 처리되어 무시됩니다</li>
              <li>• 수량은 숫자만 입력 (0 이하는 무시됨)</li>
            </ul>
          </div>

          <div className="border-2 border-dashed rounded-lg p-8 text-center">
            <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-sm text-gray-600 mb-4">
              {isUploading
                ? '파일을 처리 중입니다...'
                : '엑셀 파일을 선택하거나 이 영역에 드래그하세요'}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              className="hidden"
              disabled={isUploading}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className={`px-4 py-2 rounded-md transition-colors ${
                isUploading
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
              disabled={isUploading}
            >
              {isUploading ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  처리 중
                </div>
              ) : (
                '파일 선택'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}